import { useCallback, useRef } from "react";
import { useCanvasStore } from "./store/canvas-store";
import { CanvasGrid } from "./canvas-grid";
import { CanvasObjectsLayer } from "./canvas-objects-layer";
import { CanvasPenLayer, type PenLayerHandle } from "./canvas-pen-layer";
import { CanvasCursorsLayer } from "./canvas-cursors-layer";
import { CanvasSelectionOverlay } from "./canvas-selection-overlay";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../lib/canvas-coordinates";
import { useKeyboardPan } from "./input/use-keyboard-pan";
import { useWheelPanZoom } from "./input/use-wheel-pan-zoom";
import { usePasteHandler } from "./input/use-paste-handler";
import { useDropHandler } from "./input/use-drop-handler";
import { usePointerMode } from "./input/use-pointer-mode";
import { useGlobalShortcuts } from "./input/use-global-shortcuts";
import { useCanvasOpDispatcher } from "./use-canvas-op-dispatcher";
import { CanvasOpDispatcherProvider } from "./dispatcher-context";
import { useCursorBroadcaster } from "./use-cursor-broadcaster";
import { useCurrentActor } from "../../auth/use-current-actor";
import { useCanvasApi, type CanvasApiClient } from "../../lib/canvas-api";
import type { StrokePoint } from "../../lib/pen-stroke";

interface Props {
  readonly canvasId: string;
}

const STICKY_DEFAULT_W = 200;
const STICKY_DEFAULT_H = 160;
const TEXT_DEFAULT_W = 240;
const TEXT_DEFAULT_H = 48;
const SHAPE_DEFAULT_W = 160;
const SHAPE_DEFAULT_H = 120;
const IMAGE_DEFAULT_MAX = 480;

// 8K サイズ（7680x4320）の DOM ステージ。
// CSS transform: translate3d(panX,panY,0) scale(zoom) で全オブジェクトを一括変形する。
// 子レイヤはステージ座標で配置し、ヒットテストは DOM のイベントバブリングで処理する。
export function CanvasStage({ canvasId }: Props): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const tool = useCanvasStore((s) => s.tool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const containerRef = useRef<HTMLDivElement>(null);
  const penLayerRef = useRef<PenLayerHandle | null>(null);

  const actor = useCurrentActor();
  const canvasApi = useCanvasApi();
  const dispatcher = useCanvasOpDispatcher({ actorUserId: actor.userId });

  // ペンストローク確定時: 描画範囲を AABB に丸めて pen.stroke.commit op を送信
  const onStrokeCommit = useCallback(
    (params: { points: readonly StrokePoint[]; color: string; thickness: number }) => {
      const aabb = computeAabb(params.points, params.thickness);
      dispatcher.commitPenStroke(params.points, params.color, params.thickness, aabb);
    },
    [dispatcher]
  );

  // 付箋 / 図形 / テキストの配置
  const onPlaceObject = useCallback(
    ({ stagePoint }: { stagePoint: { x: number; y: number } }) => {
      switch (tool) {
        case "place-sticky":
          dispatcher.createObject({
            type: "sticky",
            x: stagePoint.x - STICKY_DEFAULT_W / 2,
            y: stagePoint.y - STICKY_DEFAULT_H / 2,
            w: STICKY_DEFAULT_W,
            h: STICKY_DEFAULT_H,
            text: "",
            color: "#fff5b3",
          });
          return;
        case "place-text":
          dispatcher.createObject({
            type: "text",
            x: stagePoint.x,
            y: stagePoint.y,
            w: TEXT_DEFAULT_W,
            h: TEXT_DEFAULT_H,
            text: "テキスト",
            fontSize: 16,
          });
          return;
        case "place-shape":
          dispatcher.createObject({
            type: "shape",
            shapeKind,
            x: stagePoint.x - SHAPE_DEFAULT_W / 2,
            y: stagePoint.y - SHAPE_DEFAULT_H / 2,
            w: SHAPE_DEFAULT_W,
            h: SHAPE_DEFAULT_H,
            stroke: "#1a1a2e",
          });
          return;
        default:
          return;
      }
    },
    [dispatcher, shapeKind, tool]
  );

  // クリップボード画像: presigned PUT でアップロードしてから image オブジェクトを作成
  const onPasteImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position);
    },
    [canvasApi, canvasId, dispatcher]
  );

  // クリップボード URL: OGP 取得してリンクカード化
  const onPasteUrl = useCallback(
    (url: string, position: { x: number; y: number }) => {
      void (async () => {
        try {
          const og = await canvasApi.fetchOgp(url);
          dispatcher.createObject({
            type: "link-card",
            x: position.x,
            y: position.y,
            w: 320,
            h: 200,
            url,
            title: og.title ?? url,
            description: og.description,
            imageUrl: og.image,
            siteName: og.siteName,
          });
        } catch {
          // OGP 取得失敗時は URL テキストとして配置
          dispatcher.createObject({
            type: "text",
            x: position.x,
            y: position.y,
            w: TEXT_DEFAULT_W,
            h: TEXT_DEFAULT_H,
            text: url,
            color: "#1e63d4",
          });
        }
      })();
    },
    [canvasApi, dispatcher]
  );

  const onDropImage = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceImage(canvasApi, canvasId, dispatcher, file, position);
    },
    [canvasApi, canvasId, dispatcher]
  );

  const onDropVideo = useCallback(
    (file: File, position: { x: number; y: number }) => {
      void uploadAndPlaceVideo(canvasApi, canvasId, dispatcher, file, position);
    },
    [canvasApi, canvasId, dispatcher]
  );

  // 入力ハンドラを束ねる
  useGlobalShortcuts(dispatcher);
  useKeyboardPan();
  useWheelPanZoom(containerRef);
  useDropHandler(containerRef, { onDropImage, onDropVideo });
  usePasteHandler({ onPasteImage, onPasteUrl });
  usePointerMode({ stageRef: containerRef, penLayerRef }, { onStrokeCommit, onPlaceObject });
  useCursorBroadcaster();

  return (
    <div
      ref={containerRef}
      data-testid="canvas-container"
      className={
        tool === "panning"
          ? "canvas-cursor-grabbing"
          : tool === "draw-pen"
            ? "canvas-cursor-crosshair"
            : tool === "place-sticky" || tool === "place-shape" || tool === "place-text"
              ? "canvas-cursor-crosshair"
              : "canvas-cursor-default"
      }
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      <div
        data-testid="canvas-stage"
        style={{
          position: "absolute",
          width: `${CANVAS_WIDTH_PX}px`,
          height: `${CANVAS_HEIGHT_PX}px`,
          transformOrigin: "0 0",
          transform: `translate3d(${view.panX}px, ${view.panY}px, 0) scale(${view.zoom})`,
          willChange: "transform",
          background: "var(--color-surface)",
          boxShadow: "0 0 0 1px var(--color-border)",
          pointerEvents: "none",
        }}
      >
        <CanvasGrid />
        <CanvasOpDispatcherProvider value={dispatcher}>
          <CanvasObjectsLayer />
          <CanvasSelectionOverlay />
        </CanvasOpDispatcherProvider>
        <CanvasPenLayer handleRef={penLayerRef} />
        <CanvasCursorsLayer />
      </div>
    </div>
  );
}

/**
 * ストローク座標群と太さから外接矩形を求める。pen-stroke オブジェクトの x,y,w,h に使う。
 */
function computeAabb(
  points: readonly { x: number; y: number }[],
  thickness: number
): { x: number; y: number; w: number; h: number } {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  const first = points[0];
  if (!first) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = first.x;
  let minY = first.y;
  let maxX = first.x;
  let maxY = first.y;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = thickness;
  return {
    x: minX - pad,
    y: minY - pad,
    w: maxX - minX + pad * 2,
    h: maxY - minY + pad * 2,
  };
}

/**
 * 画像ファイルを presigned PUT で添付バケットへアップロードし、
 * 完了通知で得た公開 URL を attachment.update op で全員へ伝搬する。
 *
 * 1) 即時 UX のため URL.createObjectURL でローカル URL を src にした image オブジェクトを配置
 * 2) presigned PUT で S3 にバイナリ送信
 * 3) /uploads:complete を叩き、CloudFront 公開 URL を取得（未設定なら null）
 * 4) publicUrl が取れたら attachment.update op を発行して src を永続 URL に差し替える
 *    publicUrl が null の場合（dev/mock）は blob: URL のまま（自分のセッション中のみ表示）
 */
async function uploadAndPlaceImage(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: ReturnType<typeof useCanvasOpDispatcher>,
  file: File,
  position: { x: number; y: number }
): Promise<void> {
  const { objectId, localUrl } = await placeImageWithLocalUrl(dispatcher, file, position);
  try {
    const presigned = await canvasApi.presignImageUpload({
      canvasId,
      objectId,
      contentType: file.type || "image/png",
      contentLength: file.size,
    });
    await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "content-type": file.type || "image/png" },
    });
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.updateAttachmentSrc(objectId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    }
  } catch (e) {
    console.warn("[upload] 画像アップロードに失敗、ローカル URL のまま継続", e);
  }
}

async function uploadAndPlaceVideo(
  canvasApi: CanvasApiClient,
  canvasId: string,
  dispatcher: ReturnType<typeof useCanvasOpDispatcher>,
  file: File,
  position: { x: number; y: number }
): Promise<void> {
  const { objectId, localUrl } = await placeVideoWithLocalUrl(dispatcher, file, position);
  try {
    const presigned = await canvasApi.presignVideoUpload({
      canvasId,
      objectId,
      contentType: file.type || "video/mp4",
      contentLength: file.size,
    });
    await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "content-type": file.type || "video/mp4" },
    });
    const completed = await canvasApi.notifyUploadComplete(
      canvasId,
      presigned.bucket,
      presigned.key
    );
    if (completed.publicUrl) {
      dispatcher.updateAttachmentSrc(objectId, completed.publicUrl);
      URL.revokeObjectURL(localUrl);
    }
  } catch (e) {
    console.warn("[upload] 動画アップロードに失敗、ローカル URL のまま継続", e);
  }
}

async function placeImageWithLocalUrl(
  dispatcher: ReturnType<typeof useCanvasOpDispatcher>,
  file: File,
  position: { x: number; y: number }
): Promise<{ objectId: string; localUrl: string }> {
  const localUrl = URL.createObjectURL(file);
  const dimensions = await readImageSize(localUrl);
  const placed = dispatcher.createObject({
    type: "image",
    x: position.x - dimensions.w / 2,
    y: position.y - dimensions.h / 2,
    w: dimensions.w,
    h: dimensions.h,
    src: localUrl,
    alt: file.name,
  });
  return { objectId: placed.id, localUrl };
}

async function placeVideoWithLocalUrl(
  dispatcher: ReturnType<typeof useCanvasOpDispatcher>,
  file: File,
  position: { x: number; y: number }
): Promise<{ objectId: string; localUrl: string }> {
  const localUrl = URL.createObjectURL(file);
  const placed = dispatcher.createObject({
    type: "video",
    x: position.x - 240,
    y: position.y - 135,
    w: 480,
    h: 270,
    src: localUrl,
    mimeType: file.type || "video/mp4",
  });
  return { objectId: placed.id, localUrl };
}

function readImageSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, IMAGE_DEFAULT_MAX / Math.max(img.naturalWidth, img.naturalHeight));
      resolve({ w: img.naturalWidth * ratio, h: img.naturalHeight * ratio });
    };
    img.onerror = () => resolve({ w: 320, h: 240 });
    img.src = url;
  });
}
